import { GDocTableData, IModuleParams, ParsedGoogleDocType } from './types'
import { createNoteNode } from '../nodeCreationHelpers'
import {
  createImgTag,
  createTable,
  PARAGRAPH_SPACING,
  wrapB,
  wrapP,
  wrapSpan,
  wrapTag,
} from '../html_utility'
import {
  convertPhenomTypeToImgElement,
  createImgTagForImageCode,
  csvTransform,
  getMetadataValueForKey,
  getStorylineValueForKey,
  htmlTransform,
  intTransform,
  stringTransform,
} from './gDriveUtils'

export function lessonSets(params: IModuleParams) {
  const nodeData = lessonSetNodeDatas(params)
  return nodeData.map(({ title, value }) => {
    return createNoteNode({
      title,
      value,
      studentFacing: false,
    })
  })
}

function lessonSetNodeDatas(params: IModuleParams) {
  const lessons = getLessonsForCurrentModule(params)
  const grouped = groupLessonsByLessonSets(lessons)
  return grouped.reduce<{ title: string; value: string }[]>((acc, group) => {
    return [
      ...acc,
      {
        title: getLessonSetTitle(group),
        value: getLessonSetContent(group, params),
      },
    ]
  }, [])
}

function getLessonSetTitle(lessonSet: ParsedGoogleDocType[]) {
  const { set, end, start, dayTotal } = lessonSet.reduce(
    (acc, { tableContent }, currentIndex) => {
      if (currentIndex === 0) {
        acc.set = getLessonSet(tableContent)
        acc.start = getLessonNum(tableContent)
      }
      if (currentIndex === lessonSet.length - 1) {
        acc.end = getLessonNum(tableContent)
      }
      acc.dayTotal += getDayFromTable(tableContent)
      return acc
    },
    { set: 0, start: 0, end: 0, dayTotal: 0 },
  )
  return `Lesson Set ${set}: Lessons ${start}-${end} (${dayTotal} Days)`
}

type RowData = [string, string]
function getLessonSetContent(
  lessonSet: ParsedGoogleDocType[],
  params: {
    phenomIconMap: Record<string, string>
    gDrive: Record<string, ParsedGoogleDocType>
  },
) {
  const tableData = lessonSet.reduce<RowData[]>((acc, { tableContent }) => {
    return [
      ...acc,
      getTypeRow(tableContent, params.phenomIconMap),
      getQuestionRow(tableContent),
      getPhenomenaDesignProblemRow(tableContent),
      getWhatWeDoRow(tableContent),
      getHowRepresent(tableContent),
    ]
  }, [])
  const lessonSetTable = createTable(tableData, { colWidths: [16, 84] })
  const legend = wrapP(
    wrapSpan('How students will engage with each phenomena:'),
  )
  const legendTable = createTable([
    [
      createImgTag({
        src: 'https://kiddom-media-development.s3.amazonaws.com/OUR/Math/7/phenomena_legend.png',
        attributes: { width: '95%' },
      }),
    ],
  ])
  return legend + legendTable + PARAGRAPH_SPACING + lessonSetTable
}

function getTypeRow(
  tableData: GDocTableData,
  iconMap: Record<string, string>,
): RowData {
  const imgElms = getPhenomTypes(tableData)
    .map((type) =>
      wrapP(
        convertPhenomTypeToImgElement(
          type,
          { style: 'height: 50px;' },
          iconMap,
        ),
        { style: 'width: 50px' },
      ),
    )
    .join('')
  const content =
    wrapP(wrapSpan(`${getDayFromTable(tableData)} days`)) +
    wrapTag('div', imgElms, { style: 'display: flex' })
  const header = wrapP(wrapB(`Lesson ${getLessonNum(tableData)}`))
  return [header, content]
}

function getQuestionRow(tableData: GDocTableData): RowData {
  const header = wrapP(wrapB('Question'))
  const content = getMetadataValueForKey(
    'lesson-question',
    tableData,
    stringTransform,
  )
  return [header, wrapP(wrapB(content))]
}

function getPhenomenaDesignProblemRow(tableData: GDocTableData): RowData {
  const header = 'Phenomena or Design Problem'
  const content = getMetadataValueForKey(
    'phenomena-description',
    tableData,
    stringTransform,
  )
  const imageCode = getMetadataValueForKey(
    'overview-image-id',
    tableData,
    stringTransform,
  )
  const imgPart = imageCode ? wrapP(createImgTagForImageCode(imageCode)) : ''
  return [header, imgPart + wrapP(wrapB(content))]
}

function getHowRepresent(tableData: GDocTableData): RowData {
  const header = 'How we represent it'
  const content = getStorylineValueForKey(
    'how-represent',
    tableData,
    htmlTransform,
  )
  return [header, content]
}

function getWhatWeDoRow(tableData: GDocTableData): RowData {
  const header = 'What we do and figure out'
  const content = getStorylineValueForKey(
    'what-do-and-figure-out',
    tableData,
    htmlTransform,
  )
  return [header, content]
}

function groupLessonsByLessonSets(lessons: ParsedGoogleDocType[]) {
  let lessonSet = 0
  return lessons.reduce<ParsedGoogleDocType[][]>((acc, lesson) => {
    const ls = getLessonSet(lesson.tableContent)
    if (ls !== lessonSet) {
      acc.push([])
      lessonSet = ls
    }
    const lastSet = acc.pop()!
    lastSet.push(lesson)
    return [...acc, lastSet]
  }, [])
}

function getLessonsForCurrentModule(params: IModuleParams) {
  const lessonId = params.metadata['@id'].split('-').pop()
  if (!lessonId) return []
  return Object.entries(params.gDrive)
    .filter(
      ([lessonKey]) =>
        lessonKey.startsWith(lessonId) && lessonKey.endsWith('TG'),
    )
    .sort(([k1], [k2]) => {
      return (
        parseInt(k1.split('L').pop()!, 10) - parseInt(k2.split('L').pop()!, 10)
      )
    })
    .map(([, data]) => data)
}

function getDayFromTable(tableData: GDocTableData) {
  return getMetadataValueForKey('days', tableData, intTransform)
}

function getPhenomTypes(tableData: GDocTableData) {
  return getMetadataValueForKey('phenomena-types', tableData, csvTransform)
}

function getLessonNum(tableData: GDocTableData) {
  return getMetadataValueForKey('lesson-number', tableData, intTransform)
}

function getLessonSet(tableData: GDocTableData) {
  return getMetadataValueForKey('lesson-set', tableData, intTransform)
}
